/**
 * Hook for subscribing to negotiation SSE events.
 *
 * Connects to the backend SSE endpoint and invalidates relevant
 * TanStack Query caches when events arrive.
 */

import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { API_V1 } from '../config'
import { devLog } from '../services/devLog'

export interface SSEEventData {
  [key: string]: unknown
}

interface UseNegotiationSSEOptions {
  negotiationId: number
  /** Office token for auth (EventSource can't send headers) */
  token?: string
  enabled?: boolean
  /** Callback fired for UI-relevant events (comment_added, version_created, etc.) */
  onEvent?: (eventType: string, data: SSEEventData) => void
}

export interface TypingUser {
  actor_id: string
  display_name: string
}

interface NegotiationSSEState {
  isConnected: boolean
  lastEventType: string | null
  typingUsers: TypingUser[]
}

const RECONNECT_BASE_MS = 1000
const RECONNECT_MAX_MS = 30000
const TYPING_TIMEOUT_MS = 5000

export function useNegotiationSSE({
  negotiationId,
  token,
  enabled = true,
  onEvent,
}: UseNegotiationSSEOptions): NegotiationSSEState {
  const queryClient = useQueryClient()
  const [isConnected, setIsConnected] = useState(false)
  const [lastEventType, setLastEventType] = useState<string | null>(null)
  const [typingUsers, setTypingUsers] = useState<TypingUser[]>([])
  const retryCountRef = useRef(0)
  const eventSourceRef = useRef<EventSource | null>(null)
  const typingTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  const onEventRef = useRef(onEvent)
  onEventRef.current = onEvent

  useEffect(() => {
    if (!enabled || !negotiationId) return

    let reconnectTimeout: ReturnType<typeof setTimeout> | null = null
    let isCancelled = false

    function connect() {
      if (isCancelled) return

      const params = new URLSearchParams()
      if (token) params.set('token', token)
      const queryStr = params.toString()
      const url = `${API_V1}/negotiations/${negotiationId}/events/${queryStr ? `?${queryStr}` : ''}`

      devLog.info('NegotiationSSE', `Connecting to ${url}`)

      const es = new EventSource(url, { withCredentials: true })
      eventSourceRef.current = es

      es.onopen = () => {
        devLog.info('NegotiationSSE', 'Connected')
        setIsConnected(true)
        retryCountRef.current = 0
      }

      es.onerror = () => {
        devLog.warn('NegotiationSSE', 'Connection error, scheduling reconnect')
        setIsConnected(false)
        es.close()
        eventSourceRef.current = null

        // Clear all typing indicators — they are stale after disconnect
        typingTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout))
        typingTimeoutsRef.current.clear()
        setTypingUsers([])

        if (!isCancelled) {
          const delay = Math.min(
            RECONNECT_BASE_MS * Math.pow(2, retryCountRef.current),
            RECONNECT_MAX_MS,
          )
          retryCountRef.current++
          reconnectTimeout = setTimeout(connect, delay)
        }
      }

      // Handle specific negotiation events
      const handleEvent = (eventType: string) => (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data)
          devLog.info('NegotiationSSE', `Event: ${eventType} ${JSON.stringify(data)}`)
          setLastEventType(eventType)

          // Fire UI callback for toast/notification handling
          onEventRef.current?.(eventType, data)

          // Invalidate relevant query caches
          switch (eventType) {
            case 'negotiation.version_created':
              queryClient.invalidateQueries({ queryKey: ['negotiations', negotiationId] })
              queryClient.invalidateQueries({ queryKey: ['versions', negotiationId] })
              break
            case 'negotiation.comment_added':
              queryClient.invalidateQueries({ queryKey: ['comments', negotiationId] })
              // Also invalidate flat list (backend→Word sync), search results, and negotiation detail
              queryClient.invalidateQueries({ queryKey: ['comments-flat', negotiationId] })
              queryClient.invalidateQueries({
                queryKey: ['comments-search', negotiationId],
                exact: false,
              })
              queryClient.invalidateQueries({ queryKey: ['negotiations', negotiationId] })
              break
            case 'negotiation.party_ready':
              queryClient.invalidateQueries({ queryKey: ['negotiations', negotiationId] })
              break
            case 'negotiation.party_added':
              queryClient.invalidateQueries({ queryKey: ['negotiations', negotiationId] })
              break
            case 'negotiation.status_changed':
              queryClient.invalidateQueries({ queryKey: ['negotiations', negotiationId] })
              queryClient.invalidateQueries({ queryKey: ['negotiations'] })
              break
            case 'action_item.created':
            case 'action_item.completed':
            case 'action_item.reopened':
              queryClient.invalidateQueries({ queryKey: ['action-items', negotiationId] })
              queryClient.invalidateQueries({ queryKey: ['negotiations', negotiationId] })
              break
            case 'issue.created':
            case 'issue.updated':
              queryClient.invalidateQueries({ queryKey: ['issues', negotiationId] })
              queryClient.invalidateQueries({ queryKey: ['negotiations', negotiationId] })
              break
          }
        } catch (e) {
          devLog.warn('NegotiationSSE', `Failed to parse event: ${e}`)
        }
      }

      // Typing indicator handler
      const handleTypingEvent = (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data) as { actor_id: string; display_name: string }
          devLog.info('NegotiationSSE', `Typing: ${data.display_name}`)
          setLastEventType('negotiation.typing')

          // Add or refresh typing user
          setTypingUsers((prev) => {
            const existing = prev.find((u) => u.actor_id === data.actor_id)
            if (existing) return prev // Already in list
            return [...prev, { actor_id: data.actor_id, display_name: data.display_name }]
          })

          // Clear existing timeout and set new one
          const existingTimeout = typingTimeoutsRef.current.get(data.actor_id)
          if (existingTimeout) clearTimeout(existingTimeout)

          const timeout = setTimeout(() => {
            setTypingUsers((prev) => prev.filter((u) => u.actor_id !== data.actor_id))
            typingTimeoutsRef.current.delete(data.actor_id)
          }, TYPING_TIMEOUT_MS)
          typingTimeoutsRef.current.set(data.actor_id, timeout)
        } catch (e) {
          devLog.warn('NegotiationSSE', `Failed to parse typing event: ${e}`)
        }
      }

      es.addEventListener('negotiation.version_created', handleEvent('negotiation.version_created'))
      es.addEventListener('negotiation.comment_added', handleEvent('negotiation.comment_added'))
      es.addEventListener('negotiation.party_ready', handleEvent('negotiation.party_ready'))
      es.addEventListener('negotiation.party_added', handleEvent('negotiation.party_added'))
      es.addEventListener('negotiation.status_changed', handleEvent('negotiation.status_changed'))
      es.addEventListener('action_item.created', handleEvent('action_item.created'))
      es.addEventListener('action_item.completed', handleEvent('action_item.completed'))
      es.addEventListener('action_item.reopened', handleEvent('action_item.reopened'))
      es.addEventListener('issue.created', handleEvent('issue.created'))
      es.addEventListener('issue.updated', handleEvent('issue.updated'))
      es.addEventListener('negotiation.typing', handleTypingEvent)
    }

    connect()

    return () => {
      isCancelled = true
      if (reconnectTimeout) clearTimeout(reconnectTimeout)
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }
      // Clear all typing timeouts
      typingTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout))
      typingTimeoutsRef.current.clear()
      setTypingUsers([])
      setIsConnected(false)
    }
  }, [negotiationId, token, enabled, queryClient])

  return { isConnected, lastEventType, typingUsers }
}
